import { lazy, Suspense, useRef, type ReactNode } from 'react';
import { motion, useInView } from 'framer-motion';
import { Flag, IndianRupee, Mail, PenLine, ScrollText, ShieldCheck } from 'lucide-react';
import { ReputationGauge } from '@/components/ReputationGauge';
import { useAnimatedNumber } from '@/hooks/useAnimatedNumber';
import { GlobalSpotlight, ParticleCard } from '../reactbits/MagicBento';
// matter-js (physics engine) is only needed for this one decorative widget.
const GravityPills = lazy(() => import('./GravityPills'));
import SectionDivider from './SectionDivider';

const CONTRACT_CLAUSE_PILLS = ['Scope', 'Milestones', 'IP ownership', 'Revisions', 'Late-payment interest', 'Contract ready'];

// Brand green, not MagicBento's default purple (132, 0, 255).
const BENTO_GLOW_COLOR = '60, 180, 79';

/**
 * Features — five real, shipped things. Two large tiles (the actual
 * differentiators) plus three small ones, two rows total — kept to two
 * rows deliberately so the whole section fits in one view without
 * scrolling, which a taller hero-row layout didn't.
 */

/* ── Evidence ledger — the real Activity tab, condensed ──────────────── */
function EvidenceLedgerArt() {
  const events = [
    { icon: ScrollText, color: '#9ca3af', title: 'Contract drafted', date: '28 Jul' },
    { icon: PenLine, color: '#3cb44f', title: 'Client signed the contract', date: '28 Jul' },
    { icon: IndianRupee, color: '#a78bfa', title: 'Milestone 1 paid', date: '29 Jul' },
    { icon: Flag, color: '#3cb44f', title: 'Contract completed', date: '2 Aug' },
  ];
  return (
    <div className="w-full max-w-sm">
      {events.map((e, i) => {
        const Icon = e.icon;
        return (
          <motion.div
            key={e.title}
            className="relative flex items-start gap-3 pb-4 last:pb-0"
            initial={{ opacity: 0, x: -8 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: false, amount: 0.6 }}
            transition={{ delay: i * 0.14, duration: 0.35 }}
          >
            {i < events.length - 1 && <span className="absolute left-[13px] top-7 bottom-0 w-px bg-white/10" />}
            <span
              className="relative z-10 shrink-0 w-7 h-7 rounded-full border flex items-center justify-center"
              style={{ borderColor: `${e.color}55`, backgroundColor: `${e.color}14` }}
            >
              <Icon size={13} style={{ color: e.color }} />
            </span>
            <div className="flex-1 min-w-0 pt-0.5">
              <p className="text-[13px] font-semibold text-white leading-tight">{e.title}</p>
              <p className="text-[11px] text-white/35 mt-0.5">{e.date} 2026</p>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

/* ── Credibility score — the real dashboard gauge, on solid black ────── */
function CredibilityScoreArt() {
  const ref = useRef<HTMLDivElement>(null);
  // once: false — replays the 0→850 sweep every time this scrolls back into
  // view, whether that's scrolling down into it or back up into it, not
  // just the first time.
  const inView = useInView(ref, { once: false, amount: 0.5 });
  const displayScore = useAnimatedNumber(inView ? 850 : 0, 900);
  return (
    <div ref={ref} className="w-full flex flex-col items-center rounded-xl bg-black py-3">
      <div className="w-full max-w-[160px] -mb-2">
        <ReputationGauge score={850} maxScale={1000} animated={inView} idPrefix="features-credibility" />
      </div>
      <p className="text-xl font-bold text-white tabular-nums">{displayScore}</p>
      {/* <p className="font-mono text-[9.5px] uppercase tracking-widest text-white/35">out of 1000</p> */}
    </div>
  );
}

/* ── OTP e-signature — a short story: code delivered, then verified,
   then signed. Three connected beats instead of one static icon. ────── */
function OtpSignArt() {
  const digits = ['4', '1', '9', '2'];
  const vp = { once: false, amount: 0.6 } as const;
  return (
    <div className="flex flex-col items-center w-full">
      <div className="flex items-center gap-2 mb-4">
        <motion.div
          className="w-7 h-7 rounded-full bg-white/[0.06] border border-white/15 flex items-center justify-center shrink-0"
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={vp}
          transition={{ duration: 0.3 }}
        >
          <Mail size={12} className="text-white/50" />
        </motion.div>

        {/* Delivery — a pulse travels from the envelope to the code. */}
        <div className="relative w-9 h-px bg-white/10">
          <motion.span
            className="absolute top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#3cb44f]"
            initial={{ left: '0%', opacity: 0 }}
            whileInView={{ left: '100%', opacity: [0, 1, 1, 0] }}
            viewport={vp}
            transition={{ delay: 0.25, duration: 0.5, ease: 'easeInOut' }}
          />
        </div>

        <div className="flex gap-1.5">
          {digits.map((d, i) => (
            <motion.div
              key={i}
              className="w-6 h-8 rounded-md border border-[#3cb44f]/30 bg-[#3cb44f]/[0.07] flex items-center justify-center text-[11px] font-bold text-white"
              initial={{ opacity: 0, y: 4 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={vp}
              transition={{ delay: 0.7 + i * 0.1, duration: 0.25 }}
            >
              {d}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Signature — Hoot itself stamps in once the code lands, instead of
          an abstract signature line. Uses the email-template mark
          (/email-logo.png — the green, non-interactive version) rather than
          the navbar's white cursor-tracking icon, to match what a signer
          actually sees on the confirmation email. */}
      <motion.div
        className="w-9 h-9 flex items-center justify-center"
        initial={{ opacity: 0, scale: 0.4, rotate: -25 }}
        whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
        viewport={vp}
        transition={{ delay: 1.25, type: 'spring', stiffness: 280, damping: 13 }}
      >
        <img src="/email-logo.png" alt="Email inbox icon: the signing code is sent to the client by email" className="w-8 h-8 object-contain" />
      </motion.div>

      <motion.div
        className="flex items-center gap-1.5 text-[#3cb44f] text-[11px] font-semibold mt-1.5"
        initial={{ opacity: 0, scale: 0.85 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={vp}
        transition={{ delay: 1.7, type: 'spring', stiffness: 300, damping: 14 }}
      >
        <ShieldCheck size={12} /> Verified &amp; signed
      </motion.div>
    </div>
  );
}

/* ── Dispute certificates — a proper certificate this time: crop-mark
   corners (the same detail PricingSection's plan certificates already use,
   so this reads as the same document language, not a new one), a header
   and citation, evidence converging in from the same colored nodes
   Evidence Ledger uses, then the stamp landing with real impact. ────────── */
function DisputeCertArt() {
  const nodeColors = ['#9ca3af', '#3cb44f', '#a78bfa'];
  const lines = [100, 68, 88, 50];
  const vp = { once: false, amount: 0.6 } as const;
  const corners = [
    'top-0 left-0 border-t border-l',
    'top-0 right-0 border-t border-r',
    'bottom-0 left-0 border-b border-l',
    'bottom-0 right-0 border-b border-r',
  ];
  return (
    <div className="flex flex-col items-center w-full">
      {/* Evidence converging in — same nodes, same colors, as the ledger. */}
      <div className="flex items-center gap-3 mb-2">
        {nodeColors.map((c, i) => (
          <motion.span
            key={i}
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: c }}
            initial={{ opacity: 0, y: -8 }}
            whileInView={{ opacity: [0, 1, 1, 0], y: [-8, 0, 4, 14] }}
            viewport={vp}
            transition={{ delay: i * 0.12, duration: 0.75, ease: 'easeIn' }}
          />
        ))}
      </div>

      <div className="relative w-[104px] h-20">
        {/* Crop marks draw in first — the certificate frame arriving. */}
        {corners.map((pos, i) => (
          <motion.span
            key={pos}
            className={`absolute w-2.5 h-2.5 border-[#3cb44f]/50 ${pos}`}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={vp}
            transition={{ delay: 0.15 + i * 0.06, duration: 0.25 }}
          />
        ))}

        {/* The certificate body. */}
        <motion.div
          className="absolute inset-[7px] rounded-sm border border-white/10 bg-white/[0.03] px-2.5 pt-2 pb-1.5"
          initial={{ opacity: 0.25 }}
          whileInView={{ opacity: 1 }}
          viewport={vp}
          transition={{ delay: 0.55, duration: 0.3 }}
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="font-mono text-[7px] uppercase tracking-widest text-white/40">Evidence cert.</span>
            <span className="font-mono text-[7px] text-[#3cb44f]/70">§63</span>
          </div>
          <div className="border-t border-dashed border-white/10 mb-1.5" />
          {lines.map((w, i) => (
            <motion.div
              key={i}
              className="h-[3px] rounded-full bg-white/12 mb-1 last:mb-0"
              style={{ width: `${w}%`, transformOrigin: 'left' }}
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={vp}
              transition={{ delay: 0.7 + i * 0.08, duration: 0.3 }}
            />
          ))}
        </motion.div>

        {/* Shockwave, then the stamp lands. */}
        {[0, 1].map((i) => (
          <motion.span
            key={i}
            className="absolute w-11 h-11 rounded-full border border-[#3cb44f]/40"
            style={{ right: -2, bottom: -2 }}
            initial={{ scale: 0.5, opacity: 0 }}
            whileInView={{ scale: [0.5, 1.6], opacity: [0.6, 0] }}
            viewport={vp}
            transition={{ delay: 1.3 + i * 0.15, duration: 0.8, ease: 'easeOut' }}
          />
        ))}

        <motion.div
          className="absolute -right-2 -bottom-2 w-9 h-9 rounded-full bg-[#3cb44f] flex items-center justify-center shadow-[0_0_24px_-6px_rgba(60,180,79,0.7)]"
          initial={{ scale: 1.8, y: -18, opacity: 0, rotate: -25 }}
          whileInView={{ scale: 1, y: 0, opacity: 1, rotate: -10 }}
          viewport={vp}
          transition={{ delay: 1.15, type: 'spring', stiffness: 300, damping: 14 }}
        >
          <span className="font-mono font-black text-black text-[11px]">§63</span>
        </motion.div>
      </div>

      <motion.p
        className="text-[11px] text-[#3cb44f] font-semibold mt-2.5"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={vp}
        transition={{ delay: 1.9 }}
      >
        Certificate ready
      </motion.p>
    </div>
  );
}

function Tile({
  title,
  body,
  delay,
  glow,
  className = '',
  children,
}: {
  title: string;
  body: string;
  delay: number;
  glow: string;
  className?: string;
  children: ReactNode;
}) {
  // Two elements, not one, deliberately: framer-motion's whileHover and
  // MagicBento's gsap-driven tilt/magnetism both want to own the element's
  // transform, and running both on the same node fights. The outer
  // motion.div only ever does the entrance fade-up (mount-time, done once);
  // all hover interaction — tilt, magnetism, particles, click ripple, the
  // cursor-tracked border glow — lives on the inner ParticleCard instead.
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      <ParticleCard
        className="magic-bento-card magic-bento-card--border-glow group relative rounded-2xl border border-white/10 bg-white/[0.015] p-5 md:p-6 h-full"
        glowColor={BENTO_GLOW_COLOR}
        particleCount={6}
        enableTilt
        tiltMaxDeg={3}
        enableMagnetism
        clickEffect
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-70 group-hover:opacity-100 transition-opacity duration-500"
          style={{ background: glow }}
        />
        <div className="relative flex flex-col gap-5 h-full">
          <div className="flex-1 flex items-center justify-center min-h-[5.5rem]">{children}</div>
          <div>
            <h3 className="font-bricolage font-extrabold text-base md:text-lg text-white mb-1.5">{title}</h3>
            <p className="text-white/55 text-[12.5px] leading-relaxed max-w-sm">{body}</p>
          </div>
        </div>
      </ParticleCard>
    </motion.div>
  );
}

export default function FeaturesRevamp() {
  const gridRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <SectionDivider />
      <section className="relative bg-black py-14 md:py-16">
        <GlobalSpotlight gridRef={gridRef} glowColor={BENTO_GLOW_COLOR} spotlightRadius={280} />
        <div className="px-6 max-w-6xl mx-auto">
          {/* Same docket-row family as How It Works — tag row spans this
            section's own full max-w-5xl (matching the bento grid below),
            not a narrower separately-centered block. No step count — these
            five tiles aren't a sequence, so implying an order would be
            dishonest; "Schedule" (a document's attached annex) fits a list
            that's exhaustive but unordered. Headline sits one notch smaller
            than How It Works' — this section is the catalog, not the
            opener. */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="mb-8 md:mb-10"
          >
            {/* <div className="flex items-center gap-4 mb-5">
            <span className="font-mono text-[11px] tracking-[0.3em] text-[#3cb44f]/80 uppercase shrink-0">
              Schedule
            </span>
            <span className="h-px flex-1 bg-white/10" />
            <span className="font-mono text-[11px] tracking-[0.2em] text-white/30 uppercase shrink-0">
              05 capabilities
            </span>
          </div> */}
            <h2 className="max-w-xl font-bricolage font-extrabold text-3xl md:text-5xl text-white leading-[1.1] tracking-tight text-left">
              What you <span className="font-instrumentserif italic font-normal text-accent">actually</span> get
            </h2>
          </motion.div>

          <div ref={gridRef} className="bento-section grid grid-cols-1 md:grid-cols-6 gap-4">
            <Tile
              className="md:col-span-3"
              title="Evidence ledger"
              delay={0}
              glow="radial-gradient(90% 90% at 0% 0%, rgba(60,180,79,0.08), transparent)"
              body="Every submission, approval and payment is hash-chained the moment it happens, then anchored to a public blockchain."
            >
              <EvidenceLedgerArt />
            </Tile>

            <Tile
              className="md:col-span-3"
              title="Contract builder"
              delay={0.06}
              glow="radial-gradient(90% 90% at 100% 0%, rgba(60,180,79,0.08), transparent)"
              body="Plain-language clauses for scope, milestones, IP ownership, revisions, and late-payment interest — no legal drafting required."
            >
              <Suspense fallback={<div style={{ height: 150 }} />}>
                <GravityPills labels={CONTRACT_CLAUSE_PILLS} height={150} />
              </Suspense>
            </Tile>

            <Tile
              className="md:col-span-2"
              title="Credibility score"
              delay={0.12}
              glow="radial-gradient(80% 80% at 50% 20%, rgba(60,180,79,0.1), transparent)"
              body="Built from completed contracts, on-time delivery and client reviews — a record you own."
            >
              <CredibilityScoreArt />
            </Tile>

            <Tile
              className="md:col-span-2"
              title="OTP e-signature"
              delay={0.18}
              glow="radial-gradient(80% 80% at 50% 0%, rgba(60,180,79,0.07), transparent)"
              body="Legally valid under Section 5 of the IT Act, 2000 — your client needs an inbox, not an account."
            >
              <OtpSignArt />
            </Tile>

            <Tile
              className="md:col-span-2"
              title="Dispute certificates"
              delay={0.24}
              glow="radial-gradient(80% 80% at 50% 100%, rgba(60,180,79,0.07), transparent)"
              body="Generate a Section 63 evidence certificate and demand notice directly from the record — one click."
            >
              <DisputeCertArt />
            </Tile>
          </div>
        </div>
      </section>
    </>
  );
}
